import { TextAreaField } from '@aws-amplify/ui-react'
import { UploadFileOutlined } from '@mui/icons-material'
import { Paper, Stack, Typography } from '@mui/material'
import type { TFunction } from 'i18next'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import ConfirmationButton from '@/components/inputs/ConfirmationButton'
import MainContentContainer from '@/components/layouts/MainContentContainer'
import { useAlertContext } from '@/contexts/AlertContext'
import { useBulkImportRestrictionsMutation } from '@/features/site/hooks/useBulkImportRestrictionsMutation'
import { useSiteDetails } from '@/features/site/pages/SiteDetails'
import {
  parseCommaSeparatedIds,
  validateISODateFormat,
  validateTimeFormat,
  validateUUID,
} from '@/features/site/utils/tsvValidation'
import { errorHandler } from '@/utils/errorHandler'

type ParsedRestriction = {
  resourceIds: string[]
  startDate: string
  startTime: string
  endDate: string
  endTime: string
}

// Use common translation keys shared by both bulk import pages
const COMMON_BULK_IMPORT_PREFIX = 'common.bulk_import'
const SINGLE_SITE_PREFIX = 'customer_details.tabs.res_level_unavailability.bulk_import'

const parseTsvContent = (content: string, t: TFunction<'translation'>): ParsedRestriction[] => {
  const lines = content.trim().split('\n')

  // Skip header row
  const dataLines = lines.slice(1).filter((line) => line.trim())

  if (dataLines.length === 0) {
    throw new Error(t(`${COMMON_BULK_IMPORT_PREFIX}.error.no_data_rows`))
  }

  return dataLines.map((line, index) => {
    const rowNumber = index + 2 // Account for header and 0-based index
    const columns = line.split('\t')

    if (columns.length !== 5) {
      throw new Error(
        t(`${COMMON_BULK_IMPORT_PREFIX}.error.invalid_columns`, {
          row: rowNumber,
          expected: 5,
          got: columns.length,
        }),
      )
    }

    const [resourceIdsStr, startDate, startTime, endDate, endTime] = columns

    // Parse and validate resource IDs
    const resourceIds = parseCommaSeparatedIds(resourceIdsStr)

    if (resourceIds.length === 0) {
      throw new Error(
        t(`${COMMON_BULK_IMPORT_PREFIX}.error.no_assets`, {
          row: rowNumber,
        }),
      )
    }

    // Validate each UUID
    resourceIds.forEach((id) => {
      if (!validateUUID(id)) {
        throw new Error(
          t(`${COMMON_BULK_IMPORT_PREFIX}.error.invalid_uuid`, {
            row: rowNumber,
            uuid: id,
          }),
        )
      }
    })

    // Validate dates
    if (!validateISODateFormat(startDate) || !validateISODateFormat(endDate)) {
      throw new Error(
        t(`${COMMON_BULK_IMPORT_PREFIX}.error.invalid_date`, {
          row: rowNumber,
        }),
      )
    }

    // Validate times
    if (!validateTimeFormat(startTime) || !validateTimeFormat(endTime)) {
      throw new Error(
        t(`${COMMON_BULK_IMPORT_PREFIX}.error.invalid_time`, {
          row: rowNumber,
        }),
      )
    }

    return {
      resourceIds,
      startDate,
      startTime,
      endDate,
      endTime,
    }
  })
}

export const BulkImportRestrictionsPage = () => {
  const { t } = useTranslation()
  const { siteUuid } = useParams<{ siteUuid: string }>()
  const { site } = useSiteDetails()
  const [tsvContent, setTsvContent] = useState<string>('')
  const { pushAlert } = useAlertContext()

  const { bulkImportRestrictions, isPending } = useBulkImportRestrictionsMutation(siteUuid!)

  const dialogProps = {
    title: t(`${SINGLE_SITE_PREFIX}.dialog.title`),
    description: t(`${SINGLE_SITE_PREFIX}.dialog.description`),
  }

  const onSubmit = async () => {
    try {
      const parsedRestrictions = parseTsvContent(tsvContent, t)

      await bulkImportRestrictions({
        restrictions: parsedRestrictions,
        location: site.location || '',
      })

      setTsvContent('')
      pushAlert({
        message: t(`${SINGLE_SITE_PREFIX}.success.text`),
        severity: 'success',
        title: t(`${SINGLE_SITE_PREFIX}.success.title`),
      })
    } catch (err) {
      if (err instanceof Error && !('response' in err)) {
        // It's a frontend validation error, use the message directly
        pushAlert({ message: err.message, severity: 'error' })
      } else {
        const error = errorHandler(err, t(`${COMMON_BULK_IMPORT_PREFIX}.error.unknown`))
        pushAlert({ message: error.message, severity: 'error' })
      }
    }
  }

  const placeholderText = `Assets_ids\tstart_date\tstart_hour\tend_date\tend_hour
UUID1, UUID2\t2025-09-30\t04:00\t2025-10-01\t23:59
UUID3, UUID2\t2025-09-29\t05:00\t2025-10-02\t23:59`

  return (
    <MainContentContainer>
      <Paper sx={{ p: 3 }}>
        <Stack spacing={3}>
          <Typography variant="h5">{t(`${SINGLE_SITE_PREFIX}.title`)}</Typography>

          <Typography color="text.secondary" variant="body2">
            {t(`${SINGLE_SITE_PREFIX}.description`)}
          </Typography>

          <TextAreaField
            label={t(`${COMMON_BULK_IMPORT_PREFIX}.tsv_label`)}
            placeholder={placeholderText}
            resize="vertical"
            rows={10}
            value={tsvContent}
            onChange={(e) => setTsvContent(e.target.value)}
          />

          <ConfirmationButton
            buttonProps={{
              startIcon: <UploadFileOutlined />,
              disabled: isPending || !tsvContent.trim(),
            }}
            dialogProps={dialogProps}
            onSubmit={onSubmit}
          >
            {t(`${COMMON_BULK_IMPORT_PREFIX}.submit`)}
          </ConfirmationButton>
        </Stack>
      </Paper>
    </MainContentContainer>
  )
}
